import type { StateCreator } from "zustand";
import { supabase } from "../../lib/supabase";
import { showToast } from "../../lib/toast";
import type { IRecurringTaskDefinition, RecurrenceRule, TaskUrgency, TaskImportance } from "../../types";
import type { IStoreState } from "../useStore";

export interface RecurringSlice {
  recurringDefinitions: IRecurringTaskDefinition[];

  fetchRecurringDefinitions: () => Promise<void>;
  createRecurringDefinition: (data: {
    title: string;
    description?: string;
    urgency?: TaskUrgency;
    importance?: TaskImportance;
    recurrence_rule: RecurrenceRule;
    tag_id?: string | null;
  }) => Promise<IRecurringTaskDefinition | null>;
  updateRecurringDefinition: (id: string, data: Record<string, unknown>) => Promise<void>;
  deleteRecurringDefinition: (id: string) => Promise<void>;
  pauseRecurringDefinition: (id: string) => Promise<void>;
  resumeRecurringDefinition: (id: string) => Promise<void>;
  generateRecurringInstances: () => Promise<void>;
}

export const createRecurringSlice: StateCreator<
  IStoreState,
  [["zustand/devtools", never]],
  [],
  RecurringSlice
> = (set, get) => ({
  recurringDefinitions: [],

  fetchRecurringDefinitions: async () => {
    set({ loading: { ...get().loading, recurring: true } });

    const res = await supabase.functions.invoke("manage-recurring-definition", {
      method: "GET",
    });

    if (!res.error && res.data?.definitions) {
      set({ recurringDefinitions: res.data.definitions });
    }

    set({ loading: { ...get().loading, recurring: false } });
  },

  createRecurringDefinition: async (data) => {
    const res = await supabase.functions.invoke("manage-recurring-definition", {
      body: data,
    });

    if (res.error || !res.data?.definition) {
      console.error("Failed to create recurring definition:", res.error);
      showToast("Failed to create recurring task");
      return null;
    }

    const def = res.data.definition;
    set({ recurringDefinitions: [def, ...get().recurringDefinitions] });
    return def;
  },

  updateRecurringDefinition: async (id, data) => {
    const res = await supabase.functions.invoke(`manage-recurring-definition/${id}`, {
      method: "PATCH",
      body: data,
    });

    if (res.error) {
      console.error("Failed to update recurring definition:", res.error);
      showToast("Failed to update recurring task");
      return;
    }

    if (res.data?.definition) {
      set({
        recurringDefinitions: get().recurringDefinitions.map((d) =>
          d.id === id ? res.data.definition : d
        ),
      });
    }
  },

  deleteRecurringDefinition: async (id) => {
    const res = await supabase.functions.invoke(`manage-recurring-definition/${id}`, {
      method: "DELETE",
    });

    if (!res.error) {
      set({
        recurringDefinitions: get().recurringDefinitions.filter((d) => d.id !== id),
      });
    }
  },

  pauseRecurringDefinition: async (id) => {
    const res = await supabase.functions.invoke(`manage-recurring-definition/${id}/pause`, {
      method: "POST",
    });

    if (!res.error) {
      set({
        recurringDefinitions: get().recurringDefinitions.map((d) =>
          d.id === id ? { ...d, is_active: false } : d
        ),
      });
    }
  },

  resumeRecurringDefinition: async (id) => {
    const res = await supabase.functions.invoke(`manage-recurring-definition/${id}/resume`, {
      method: "POST",
    });

    if (!res.error) {
      set({
        recurringDefinitions: get().recurringDefinitions.map((d) =>
          d.id === id ? { ...d, is_active: true } : d
        ),
      });
    }
  },

  generateRecurringInstances: async () => {
    await supabase.functions.invoke("generate-recurring-instances");
  },
});
